// Bound to the requester's identity and parked until a signed-in identity can
// verify it — a Google redirect tears the app down before that identity exists.

const ENROLLMENT_INTENT_STORAGE_KEY = 'zippedi.mfa.enrollmentIntent';

// The enrollment vocabulary, not the challenge one: these name the screens in
// ENROLLMENT_VIEW_BY_FACTOR, where the phone factor is 'sms', not 'phone'.
const ENROLLABLE_FACTORS = ['sms', 'totp'];

// Past this window the user walked away; a later sign-in must not be diverted
// into an enrollment nobody remembers asking for.
const INTENT_MAX_AGE_MS = 10 * 60 * 1000;

// Both halves required: heimdal has no tenant-less user, so a half-known
// identity would only ever match another failure, never a real account.
const sameIdentity = (stored, identity) =>
  stored.uid === identity.uid && stored.tenantId === identity.tenantId;

const isCompleteIdentity = identity =>
  Boolean(identity) && Boolean(identity.uid) && Boolean(identity.tenantId);

function clearEnrollmentIntent() {
  try {
    window.localStorage.removeItem(ENROLLMENT_INTENT_STORAGE_KEY);
  } catch {
    // Storage disabled: there is nothing parked to clear either.
  }
}

export function rememberEnrollmentIntent({ factor, uid, tenantId } = {}) {
  if (!ENROLLABLE_FACTORS.includes(factor)) return;
  // Without a full owner the request could only ever be applied blindly, which
  // is the very thing takeEnrollmentIntentFor exists to prevent.
  if (!isCompleteIdentity({ uid, tenantId })) return;
  try {
    window.localStorage.setItem(
      ENROLLMENT_INTENT_STORAGE_KEY,
      JSON.stringify({ factor, uid, tenantId, createdAt: Date.now() }),
    );
  } catch {
    // Storage disabled (private mode / quota): the request degrades to the
    // opt-in checkbox the challenge screen already carries.
  }
}

// Anything unreadable, malformed or past its window is dropped on sight, so a
// leftover from an older shape cannot sit in storage forever.
function readEnrollmentIntent() {
  let raw;
  try {
    raw = window.localStorage.getItem(ENROLLMENT_INTENT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let stored;
  try {
    stored = JSON.parse(raw);
  } catch {
    clearEnrollmentIntent();
    return null;
  }
  const isUsable =
    stored !== null &&
    typeof stored === 'object' &&
    ENROLLABLE_FACTORS.includes(stored.factor) &&
    isCompleteIdentity(stored) &&
    typeof stored.createdAt === 'number' &&
    Date.now() - stored.createdAt < INTENT_MAX_AGE_MS;
  if (!isUsable) {
    clearEnrollmentIntent();
    return null;
  }
  return stored;
}

// Does not clear until an identity exists: a Google redirect tears the app
// down before the mount that receives it can supply one.
export function takeEnrollmentIntentFor(resolveIdentity) {
  const stored = readEnrollmentIntent();
  if (!stored) return null;
  const identity = resolveIdentity();
  // Incomplete identity: the sign-in has not settled yet, so the request
  // waits rather than being compared.
  if (!isCompleteIdentity(identity)) return null;
  if (!sameIdentity(stored, identity)) {
    // Another account on this browser. The request is not theirs to inherit,
    // and the account that made it is no longer the one signing in.
    clearEnrollmentIntent();
    return null;
  }
  clearEnrollmentIntent();
  return stored.factor;
}
